import React from 'react';
import * as d3 from 'd3';
import { nodeSymbolType } from '../../../pedigree-tool/pedigree/reproSymbol';

// Pedigree notation for a tile, drawn from the SAME shape decision the canvas
// uses (nodeSymbolType) rather than a second set of hand-drawn shapes. Pass a
// minimal `{ gender }` and the tile can never disagree with the chart about what
// a son or a sister looks like.
//
// Not in the plan's file table, but shared by RelativeTile and ReproLauncher —
// duplicating it into both was the alternative.
//
// Every symbol here is decorative: the accessible name comes from the tile's own
// label plus scope, so the svg is aria-hidden.

const STROKE = 1.75;

// The glyph box, as a multiple of the symbol size. Enough for the stroke and the
// diamond's corners with a little air, without the 2x the first draft used.
const BOX = 1.65;

/** A single person symbol: square (male), circle (female), diamond (unknown). */
export const PersonSymbol = ({ gender = 'unknown', size = 17, color }) => {
  const path = d3
    .symbol()
    .type(nodeSymbolType({ gender }))
    .size(size * size)();
  const box = size * BOX;
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={box}
      height={box}
      viewBox={`${-box / 2} ${-box / 2} ${box} ${box}`}
    >
      <path
        d={path}
        fill="none"
        stroke={color || 'currentColor'}
        strokeWidth={STROKE}
      />
    </svg>
  );
};

/**
 * The mating notation — a square and a circle joined by a horizontal line. This
 * is what makes the Spouse tile read as "add a partner to this person" without
 * help text, and it is deliberately NOT a person symbol: adding a spouse creates
 * a relationship, not just an individual.
 */
export const SpouseSymbol = ({ size = 13, color }) => {
  const gap = size * 1.9;
  const sq = d3
    .symbol()
    .type(nodeSymbolType({ gender: 'male' }))
    .size(size * size)();
  const ci = d3
    .symbol()
    .type(nodeSymbolType({ gender: 'female' }))
    .size(size * size)();
  const stroke = color || 'currentColor';
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={gap * 2 + size}
      height={size * BOX}
      viewBox={`${-gap / 2 - size} ${(-size * BOX) / 2} ${gap * 2 + size} ${
        size * BOX
      }`}
    >
      <line
        x1={-gap / 2}
        y1={0}
        x2={gap / 2}
        y2={0}
        stroke={stroke}
        strokeWidth={STROKE}
      />
      <g transform={`translate(${-gap / 2}, 0)`}>
        <path d={sq} fill="none" stroke={stroke} strokeWidth={STROKE} />
      </g>
      <g transform={`translate(${gap / 2}, 0)`}>
        <path d={ci} fill="none" stroke={stroke} strokeWidth={STROKE} />
      </g>
    </svg>
  );
};

/**
 * A reproductive node's symbol with its Bennett interior letter — 'P' for an
 * ongoing pregnancy, 'D' for a gamete donor. The letters come from the same
 * vocabulary reproGlyphLetter emits on the canvas.
 *
 * The donor glyph deliberately uses the sex-unknown diamond shape rather than a
 * square or circle: the gamete type is not chosen until the form opens, and
 * reproSatelliteGenderShape derives the donor's shape FROM that type (sperm → □,
 * ovum → ○). A launcher that guessed one would show the wrong notation half the
 * time.
 */
export const ReproSymbol = ({ letter, size = 21, color }) => {
  const path = d3
    .symbol()
    .type(nodeSymbolType({ gender: 'unknown' }))
    .size(size * size)();
  const box = size * BOX;
  const stroke = color || 'currentColor';
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={box}
      height={box}
      viewBox={`${-box / 2} ${-box / 2} ${box} ${box}`}
    >
      <path d={path} fill="none" stroke={stroke} strokeWidth={STROKE} />
      <text
        x={0}
        y={0}
        textAnchor="middle"
        dominantBaseline="central"
        // A diamond's inscribed area is much smaller than its width, so the letter
        // has to be a smaller fraction of the symbol than it would inside a square
        // — at 0.72 the P and D were touching the edges.
        fontSize={size * 0.42}
        fontWeight={700}
        fill={stroke}
        stroke="none"
      >
        {letter}
      </text>
    </svg>
  );
};
